import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { GLOSSARY_TERMS, getTermBySlug } from "@/lib/glossary";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/glossary/$slug")({
  loader: ({ params }) => {
    const term = getTermBySlug(params.slug);
    if (!term) throw notFound();
    return term;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const url = `${SITE.url}/glossary/${params.slug}`;
    const title = `${loaderData.term} — Local SEO Glossary`;
    const desc = loaderData.definition.slice(0, 158);
    return {
      meta: [
        { title: `${title} | ${SITE.name}` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: loaderData.term,
            alternateName: loaderData.also ? loaderData.also.split(",").map((s) => s.trim()) : undefined,
            description: loaderData.definition,
            url,
            inDefinedTermSet: `${SITE.url}/glossary`,
            publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Glossary", item: `${SITE.url}/glossary` },
              { "@type": "ListItem", position: 3, name: loaderData.term, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: TermPage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 sm:px-8 py-24 text-center">
        <p className="eyebrow">Not found</p>
        <h1 className="headline-display mt-3 text-4xl">Term not in our glossary yet</h1>
        <p className="mt-4 text-muted-foreground">Browse the full glossary or search above.</p>
        <Link to="/glossary" className="mt-6 inline-block underline">Back to glossary</Link>
      </main>
      <SiteFooter />
    </div>
  ),
});

function TermPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <TermBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

function TermBody() {
  const params = Route.useParams();
  const term = Route.useLoaderData();
  const url = `${SITE.url}/glossary/${params.slug}`;
  const related = GLOSSARY_TERMS.filter((t) => t.slug !== term.slug).slice(0, 8);

  return (
    <article className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-accent">Home</Link></li>
          <li aria-hidden>/</li>
          <li><Link to="/glossary" className="hover:text-accent">Glossary</Link></li>
          <li aria-hidden>/</li>
          <li>{term.term}</li>
        </ol>
      </nav>
      <header className="mt-6">
        <p className="eyebrow">Definition</p>
        <h1 className="headline-display mt-3 text-5xl">{term.term}</h1>
        {term.also && (
          <p className="mt-2 text-sm uppercase tracking-wider text-muted-foreground">
            Also known as: {term.also}
          </p>
        )}
      </header>

      <p className="mt-8 text-xl leading-relaxed text-foreground/90 font-serif">
        {term.definition}
      </p>

      <div className="mt-10 flex items-center gap-3 border-t border-b border-border py-5">
        <CiteThis title={`${term.term} — Local SEO Glossary`} url={url} author={`${SITE.name} Newsroom`} />
        <p className="text-xs text-muted-foreground">
          Free to use with a link back to this page.
        </p>
      </div>

      <section className="mt-12">
        <p className="eyebrow">More terms</p>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {related.map((t) => (
            <li key={t.slug}>
              <Link
                to="/glossary/$slug"
                params={{ slug: t.slug }}
                className="block rounded-sm border border-border bg-paper-soft px-3 py-2 text-sm hover:border-foreground transition-colors"
              >
                {t.term}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <NewsletterCTA />
    </article>
  );
}
